/* eslint-disable react/no-unescaped-entities */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowLeft, KeyRound, Eye, EyeOff, RotateCcw, Save, AlertCircle,
  CheckCircle2, Shield, Copy, Database, Server,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
const REVEAL_PHRASE = 'I understand this reveals a secret';

const INTEGRATIONS = [
  { key: 'amazon_public', title: 'Amazon Public SP-App', testid: 'integration-amazon_public' },
];

export default function SuperAdminPlatformCredentialsPage() {
  const navigate = useNavigate();
  const [active, setActive] = useState(INTEGRATIONS[0].key);

  return (
    <div className="min-h-screen bg-wt-bg text-wt-text-primary">
      <div className="max-w-5xl mx-auto p-6 space-y-6">
        <button
          onClick={() => navigate('/super-admin')}
          className="text-sm text-wt-text-muted hover:text-wt-gold flex items-center gap-1"
          data-testid="back-to-super-admin"
        >
          <ArrowLeft className="w-4 h-4" /> Back to Super Admin
        </button>

        <div>
          <h1 className="font-playfair text-3xl font-bold flex items-center gap-3">
            <Shield className="w-7 h-7 text-wt-gold" />
            Platform Credentials
          </h1>
          <p className="text-wt-text-secondary mt-1 text-sm">
            Manage shared integration credentials used across all tenants.
            Database overrides take precedence over <code>.env</code> values —
            clear a field to fall back to env.
          </p>
        </div>

        {/* Integration tabs */}
        <div className="flex gap-2 border-b border-wt-border">
          {INTEGRATIONS.map(t => (
            <button
              key={t.key}
              onClick={() => setActive(t.key)}
              className={`px-4 py-2 text-sm border-b-2 transition-colors ${
                active === t.key
                  ? 'border-wt-gold text-wt-gold'
                  : 'border-transparent text-wt-text-muted hover:text-wt-text-primary'
              }`}
              data-testid={`tab-${t.key}`}
            >
              {t.title}
            </button>
          ))}
        </div>

        <IntegrationPanel integrationKey={active} />
      </div>
    </div>
  );
}

const IntegrationPanel = ({ integrationKey }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dirty, setDirty] = useState({});           // field_key -> new value
  const [saving, setSaving] = useState(false);
  const [revealed, setRevealed] = useState({});     // field_key -> plaintext (after confirm)
  const [revealTarget, setRevealTarget] = useState(null);
  const [audit, setAudit] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, a] = await Promise.all([
        axios.get(`${API}/admin/platform-credentials/${integrationKey}`, { headers: authHeaders() }),
        axios.get(`${API}/admin/platform-credentials/${integrationKey}/audit?limit=20`, { headers: authHeaders() }),
      ]);
      setData(r.data);
      setAudit(a.data.entries || []);
      setDirty({});
      setRevealed({});
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load');
    } finally { setLoading(false); }
  }, [integrationKey]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!Object.keys(dirty).length) return;
    setSaving(true);
    try {
      const r = await axios.put(`${API}/admin/platform-credentials/${integrationKey}`,
        { values: dirty }, { headers: authHeaders() });
      toast.success(`Saved ${r.data.changed.length} field${r.data.changed.length === 1 ? '' : 's'}`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const resetField = async (field_key) => {
    if (!window.confirm(`Reset ${field_key} to .env value? This deletes the database override.`)) return;
    try {
      await axios.put(`${API}/admin/platform-credentials/${integrationKey}`,
        { values: { [field_key]: '' } }, { headers: authHeaders() });
      toast.success('Field reset to env value');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reset failed');
    }
  };

  const reveal = async (field_key) => {
    try {
      const r = await axios.post(
        `${API}/admin/platform-credentials/${integrationKey}/reveal`,
        { field_key, confirm: REVEAL_PHRASE },
        { headers: authHeaders() },
      );
      setRevealed(s => ({ ...s, [field_key]: r.data.value || '' }));
      toast.success('Secret revealed (visible until you navigate away)');
      setRevealTarget(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reveal failed');
    }
  };

  if (loading) {
    return <div className="p-8 text-center text-wt-text-muted">Loading…</div>;
  }
  if (!data) return null;

  return (
    <div className="space-y-4">
      <Card className="bg-wt-surface border-wt-border">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <KeyRound className="w-5 h-5 text-wt-gold" />
            {data.label}
          </CardTitle>
          {data.description && (
            <p className="text-sm text-wt-text-muted mt-1">{data.description}</p>
          )}
        </CardHeader>
        <CardContent className="space-y-4">
          {data.fields.map(f => (
            <FieldRow
              key={f.key}
              field={f}
              draft={dirty[f.key]}
              revealedValue={revealed[f.key]}
              onChange={(v) => setDirty(d => ({ ...d, [f.key]: v }))}
              onReset={() => resetField(f.key)}
              onRequestReveal={() => setRevealTarget(f)}
              onHide={() => setRevealed(s => { const n = { ...s }; delete n[f.key]; return n; })}
              data-testid={`field-${f.key}`}
            />
          ))}

          <div className="flex justify-between items-center pt-2 border-t border-wt-border">
            <div className="text-xs text-wt-text-muted">
              {Object.keys(dirty).length > 0
                ? <span className="text-amber-400">{Object.keys(dirty).length} unsaved change(s)</span>
                : 'No pending changes'}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={load} disabled={saving} data-testid="cancel-btn">
                Cancel
              </Button>
              <Button
                onClick={save}
                disabled={!Object.keys(dirty).length || saving}
                className="bg-wt-gold hover:bg-wt-gold/90 text-black"
                data-testid="save-credentials-btn"
              >
                <Save className="w-4 h-4 mr-1" /> {saving ? 'Saving…' : 'Save changes'}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Audit log */}
      <Card className="bg-wt-surface border-wt-border">
        <CardHeader>
          <CardTitle className="text-base">Recent activity</CardTitle>
        </CardHeader>
        <CardContent>
          {audit.length === 0 ? (
            <p className="text-sm text-wt-text-muted">No changes logged yet.</p>
          ) : (
            <ul className="space-y-2 text-sm" data-testid="audit-list">
              {audit.map((e, i) => (
                <li key={i} className="flex items-start gap-2 py-1 border-b border-wt-border last:border-0">
                  <Database className="w-3.5 h-3.5 text-wt-text-muted mt-1 flex-none" />
                  <div className="flex-1">
                    <div className="text-wt-text-secondary">
                      <b>{e.actor_name || e.actor_id || 'unknown'}</b>{' '}
                      {(e.fields_revealed || []).length > 0
                        ? <>revealed <span className="font-mono text-rose-300">{e.fields_revealed.join(', ')}</span></>
                        : <>updated <span className="font-mono text-emerald-300">{(e.fields_changed || []).join(', ')}</span></>}
                    </div>
                    <div className="text-[10px] text-wt-text-muted font-mono">
                      {new Date(e.at).toLocaleString()}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {revealTarget && (
        <RevealConfirmModal
          field={revealTarget}
          onClose={() => setRevealTarget(null)}
          onConfirm={() => reveal(revealTarget.key)}
        />
      )}
    </div>
  );
};

const FieldRow = ({ field, draft, revealedValue, onChange, onReset, onRequestReveal, onHide }) => {
  const hasDraft = draft !== undefined;
  const currentDisplay = hasDraft
    ? draft
    : (field.secret ? (revealedValue ?? field.masked_value) : field.masked_value);

  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <Label className="text-sm font-medium">{field.label}</Label>
        <div className="flex items-center gap-2">
          <SourceBadge field={field} />
          {field.has_override && (
            <button
              onClick={onReset}
              className="text-[10px] text-amber-400 hover:text-amber-300 flex items-center gap-1"
              data-testid={`reset-${field.key}`}
            >
              <RotateCcw className="w-3 h-3" /> Reset to env
            </button>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Input
          value={hasDraft ? draft : (field.secret && !revealedValue ? '' : currentDisplay)}
          placeholder={field.secret && !revealedValue
            ? (field.masked_value || 'Not set')
            : (field.is_set ? '' : 'Not set')}
          onChange={(e) => onChange(e.target.value)}
          className="flex-1 font-mono text-xs"
          type={field.secret && !revealedValue && !hasDraft ? 'password' : 'text'}
          data-testid={`input-${field.key}`}
        />
        {field.secret && (
          revealedValue
            ? (
                <Button size="sm" variant="ghost" onClick={onHide} data-testid={`hide-${field.key}`}>
                  <EyeOff className="w-4 h-4" />
                </Button>
              )
            : (
                <Button size="sm" variant="ghost" onClick={onRequestReveal} disabled={!field.is_set} data-testid={`reveal-${field.key}`}>
                  <Eye className="w-4 h-4" />
                </Button>
              )
        )}
        {currentDisplay && (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              navigator.clipboard.writeText(hasDraft ? draft : (revealedValue ?? field.masked_value));
              toast.success('Copied');
            }}
            data-testid={`copy-${field.key}`}
          >
            <Copy className="w-4 h-4" />
          </Button>
        )}
      </div>
      <p className="text-[10px] text-wt-text-muted font-mono pl-1">{field.key}</p>
    </div>
  );
};

const SourceBadge = ({ field }) => {
  if (field.source === 'database') {
    return (
      <Badge className="bg-emerald-500/20 text-emerald-300 border-emerald-500/30 text-[10px]">
        <Database className="w-3 h-3 mr-1" /> DB override
      </Badge>
    );
  }
  if (field.source === 'env') {
    return (
      <Badge className="bg-blue-500/20 text-blue-300 border-blue-500/30 text-[10px]">
        <Server className="w-3 h-3 mr-1" /> .env
      </Badge>
    );
  }
  return (
    <Badge className="bg-rose-500/20 text-rose-300 border-rose-500/30 text-[10px]">
      <AlertCircle className="w-3 h-3 mr-1" /> Unset
    </Badge>
  );
};

const RevealConfirmModal = ({ field, onClose, onConfirm }) => {
  const [phrase, setPhrase] = useState('');
  const ok = phrase.trim() === REVEAL_PHRASE;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="reveal-confirm-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-amber-400" />
            Reveal secret: {field.label}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <p className="text-wt-text-secondary">
            Revealing this secret will display it in plaintext. The action is logged in the audit trail.
            Make sure no one is watching your screen.
          </p>
          <div>
            <Label className="text-xs">
              Type{' '}
              <span className="font-mono text-amber-400">"{REVEAL_PHRASE}"</span>{' '}
              to confirm
            </Label>
            <Input
              value={phrase}
              onChange={(e) => setPhrase(e.target.value)}
              className="font-mono text-xs"
              data-testid="reveal-confirm-input"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={onConfirm} disabled={!ok} className="bg-rose-500/80 hover:bg-rose-500 text-white" data-testid="reveal-confirm-btn">
            {ok ? <><CheckCircle2 className="w-4 h-4 mr-1" /> Reveal</> : 'Type the phrase exactly'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
